import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { LookupsService, OnboardingClientService } from '@automatick/shared-ng';
import { ModalService } from '@seech/ux-ng';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  takeUntil,
} from 'rxjs/operators';
import { OnboardingStepperModalComponent } from '../../modules/shared/onboarding-stepper-modal/onboarding-stepper-modal.component';
import { ConfirmModalComponent } from '../../modules/shared/confirm-modal/confirm-modal.component';
import {
  AppStateService,
  AppTelemetryService,
  UserNotificationService,
  WebChatService,
  classifyAppTelemetryRoute,
} from '../../services';
import { NotificationCardsComponent } from '../notification-cards/notification-cards.component';
import {
  LayoutCommand,
  LayoutViewModel,
  createLayoutRouteContext,
  projectLayoutViewModel,
  readExactLayoutIdentifier,
} from './layout-presentation.models';
import { LayoutMobileStatusComponent } from './layout-mobile-status.component';
import { LayoutProfileMenuComponent } from './layout-profile-menu.component';
import { LayoutSidebarComponent } from './layout-sidebar.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    AsyncPipe,
    LayoutMobileStatusComponent,
    LayoutProfileMenuComponent,
    LayoutSidebarComponent,
    NotificationCardsComponent,
    RouterModule,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements OnInit, OnDestroy {
  private readonly appStateService = inject(AppStateService);
  private readonly router = inject(Router);
  private readonly telemetryService = inject(AppTelemetryService);
  private readonly webChatService = inject(WebChatService);
  private readonly userNotificationService = inject(UserNotificationService);
  private readonly modalService = inject(ModalService);
  private readonly lookupsService = inject(LookupsService);
  private readonly onboardingClient = inject(OnboardingClientService);

  private readonly sidebar = viewChild(LayoutSidebarComponent);
  private readonly mobileStatus = viewChild(LayoutMobileStatusComponent);
  private readonly destroy$ = new Subject<void>();
  private readonly routeContext = new BehaviorSubject(
    createLayoutRouteContext(this.router.url),
  );
  private readonly onboardingCheckedWorkspaceIds = new Set<string>();
  private isOnboardingModalOpen = false;
  private destroyed = false;

  readonly mobileNavOpen = signal(false);
  readonly sidebarCollapsed = signal(false);
  readonly viewModel$: Observable<LayoutViewModel>;

  constructor() {
    this.viewModel$ = combineLatest([
      this.appStateService.state$,
      this.webChatService.state$,
      this.routeContext,
    ]).pipe(
      map(([appState, chatState, route]) =>
        projectLayoutViewModel({
          appState,
          chatState,
          route,
          sidebarNavigation: this.appStateService.getSidebarNavigation(
            appState.activeRole,
          ),
          menuNavigation: this.appStateService.getMenuNavigation(
            appState.activeRole,
          ),
          now: new Date(),
        }),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        this.telemetryService.track(
          'route.view',
          classifyAppTelemetryRoute(event.urlAfterRedirects),
        );
        this.routeContext.next(
          createLayoutRouteContext(event.urlAfterRedirects),
        );
        this.syncActiveConversationNotificationContext();
      });
  }

  ngOnInit(): void {
    this.telemetryService.track('workspace.bootstrap.requested', {
      trigger: 'application-start',
    });
    this.appStateService.bootstrapWorkspaceContext();
    this.lookupsService.load().pipe(takeUntil(this.destroy$)).subscribe();
    this.userNotificationService.bootstrap();

    this.appStateService.state$
      .pipe(
        map((state) => readExactLayoutIdentifier(state.activeWorkspaceId)),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((workspaceId) => {
        this.webChatService.loadProjectsForWorkspace(workspaceId);
        this.checkOnboarding(workspaceId);
      });

    this.webChatService.bootstrap();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.routeContext.complete();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.isMobileViewport()) {
      this.mobileNavOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onDocumentEscape(event: Event): void {
    if (!this.mobileNavOpen()) {
      return;
    }

    event.preventDefault();
    this.closeMobileNavigation(true);
  }

  @HostListener('document:visibilitychange')
  onDocumentVisibilityChange(): void {
    this.syncActiveConversationNotificationContext();
  }

  handleCommand(command: LayoutCommand): void {
    switch (command.kind) {
      case 'navigate':
        void this.router.navigateByUrl(command.route);
        this.closeMobileNavigation(false);
        return;
      case 'toggle-sidebar':
        this.sidebarCollapsed.update((collapsed) => !collapsed);
        return;
      case 'expand-sidebar':
        this.sidebarCollapsed.set(false);
        return;
      case 'open-mobile-navigation':
        this.openMobileNavigation();
        return;
      case 'close-mobile-navigation':
        this.closeMobileNavigation(true);
        return;
      case 'create-project':
        this.openCreateProjectModal();
        return;
      case 'select-project':
        if (command.projectId === null) {
          this.webChatService.setProjectScope(null);
          return;
        }
        this.dispatchProjectScope(command.projectId);
        return;
      case 'search-conversations':
        this.openConversationSearch(command.sessionId);
        return;
      case 'create-conversation':
        this.createConversation();
        return;
      case 'select-conversation':
        this.selectConversation(command.sessionId);
        return;
      case 'rename-conversation':
        this.openRenameConversationModal(
          command.sessionId,
          command.displayedTitle,
        );
        return;
      case 'archive-conversation':
        this.openArchiveConversationModal(command.sessionId);
        return;
      case 'delete-conversation':
        this.openDeleteConversationModal(command.sessionId);
        return;
      default:
        assertUnreachable(command);
    }
  }

  private openMobileNavigation(): void {
    this.mobileNavOpen.set(true);
    queueMicrotask(() => {
      if (!this.destroyed) {
        this.sidebar()?.focusMobileCloseButton();
      }
    });
  }

  private dispatchProjectScope(projectId: string): void {
    const exactProjectId = readExactLayoutIdentifier(projectId);
    if (exactProjectId) {
      this.webChatService.setProjectScope(exactProjectId);
    }
  }

  private closeMobileNavigation(restoreFocus: boolean): void {
    if (!this.mobileNavOpen()) {
      return;
    }

    this.mobileNavOpen.set(false);
    if (restoreFocus) {
      this.mobileStatus()?.focusNavigationButton();
    }
  }

  private createConversation(): void {
    this.webChatService.createNewSession();
    void this.router.navigate(['/home']);
    this.closeMobileNavigation(false);
  }

  private openConversationSearch(sessionId: string): void {
    const exactSessionId = readExactLayoutIdentifier(sessionId);
    if (!exactSessionId) {
      return;
    }

    void this.router.navigate(['/chat', exactSessionId], {
      queryParams: { conversationSearch: 'open' },
      queryParamsHandling: 'merge',
    });
    this.closeMobileNavigation(false);
  }

  private selectConversation(sessionId: string): void {
    const exactSessionId = readExactLayoutIdentifier(sessionId);
    if (!exactSessionId) {
      return;
    }

    void this.router.navigate(['/chat', exactSessionId]);
    this.closeMobileNavigation(false);
  }

  private openCreateProjectModal(): void {
    const ref = this.modalService.open(ConfirmModalComponent, {
      data: {
        title: 'Create project',
        message: 'Enter a name for this project.',
        confirmLabel: 'Create',
        cancelLabel: 'Cancel',
        variant: 'primary',
        inputValue: '',
        inputPlaceholder: 'Project name',
      },
      animation: false,
      nonInvasive: true,
    });

    ref.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: string | null) => {
        const name = result?.trim();
        if (name) {
          this.webChatService.createProject(name);
        }
      });
  }

  private openRenameConversationModal(
    sessionId: string,
    displayedTitle: string,
  ): void {
    const exactSessionId = readExactLayoutIdentifier(sessionId);
    if (!exactSessionId) {
      return;
    }

    const ref = this.modalService.open(ConfirmModalComponent, {
      data: {
        title: 'Rename conversation',
        message: 'Enter a new name for this conversation.',
        confirmLabel: 'Rename',
        cancelLabel: 'Cancel',
        variant: 'primary',
        inputValue: displayedTitle,
        inputPlaceholder: 'Conversation name',
      },
      animation: false,
      nonInvasive: true,
    });

    ref.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: string | null) => {
        if (result !== null && result !== undefined) {
          this.webChatService.renameSession(exactSessionId, result);
        }
      });
  }

  private openArchiveConversationModal(sessionId: string): void {
    const exactSessionId = readExactLayoutIdentifier(sessionId);
    if (!exactSessionId) {
      return;
    }

    const ref = this.modalService.open(ConfirmModalComponent, {
      data: {
        title: 'Archive conversation',
        message: 'Are you sure you want to archive this conversation?',
        confirmLabel: 'Archive',
        cancelLabel: 'Cancel',
        variant: 'warning',
      },
      animation: false,
      nonInvasive: true,
    });

    ref.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean | null) => {
        if (result) {
          this.webChatService.archiveSession(exactSessionId);
        }
      });
  }

  private openDeleteConversationModal(sessionId: string): void {
    const exactSessionId = readExactLayoutIdentifier(sessionId);
    if (!exactSessionId) {
      return;
    }

    const ref = this.modalService.open(ConfirmModalComponent, {
      data: {
        title: 'Delete conversation',
        message:
          'Are you sure you want to delete this conversation permanently? This action cannot be undone.',
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        variant: 'danger',
      },
      animation: false,
      nonInvasive: true,
    });

    ref.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean | null) => {
        if (!result) {
          return;
        }

        const isActive =
          this.webChatService.stateSnapshot?.session?.sessionId ===
          exactSessionId;
        this.webChatService.deleteSession(exactSessionId);
        if (isActive) {
          void this.router.navigate(['/home']);
        }
      });
  }

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  }

  private syncActiveConversationNotificationContext(): void {
    const route = this.routeContext.value;
    this.userNotificationService.setActiveConversationSession(
      route.isChatRoute && !route.hasInvalidChatSessionId
        ? route.chatSessionId
        : null,
    );
  }

  private checkOnboarding(workspaceId: string | null): void {
    if (!workspaceId || this.onboardingCheckedWorkspaceIds.has(workspaceId)) {
      return;
    }

    this.onboardingCheckedWorkspaceIds.add(workspaceId);
    this.onboardingClient
      .get()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (status) => {
          if (!status.shouldShow || this.isOnboardingModalOpen) {
            return;
          }

          this.isOnboardingModalOpen = true;
          const ref = this.modalService.open(OnboardingStepperModalComponent, {
            animation: false,
            nonInvasive: true,
            ignoreBackdropClick: true,
          });
          ref.onClose.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.isOnboardingModalOpen = false;
          });
        },
        error: () => {
          this.isOnboardingModalOpen = false;
        },
      });
  }
}

function assertUnreachable(value: never): never {
  void value;
  throw new Error('Unsupported layout command.');
}
